/**
 * LMSFacultyDashboardPage.jsx — Delegation Readiness Console
 */
import { useEffect, useState } from "react";
import DashboardLayout from "../../components/DashboardLayout";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { School, Users, BookOpen, Award, Trophy } from "lucide-react";
import { Hero, Kpi, Loading, EmptyState } from "./LMSTrainerDashboardPage";

export default function LMSFacultyDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/lms/faculty/dashboard");
        setData(r.data);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <DashboardLayout title="School Delegation">
      <div data-testid="lmsx-faculty-dashboard" className="space-y-6 pb-12">
        <Hero
          eyebrow="Faculty Advisor · Diplomexa Academy"
          title="Lead your delegation to a podium finish"
          subtitle="Track readiness across your delegates, spot weak skills early, and align prep before conference day."
        />
        {loading || !data ? <Loading /> : data.error ? (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-800">{data.error}</div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <Kpi icon={Users} label="Delegates" value={data.delegate_count ?? 0} testid="lmsx-faculty-delegates" />
              <Kpi icon={BookOpen} label="Course enrolments" value={data.courses_enrolled ?? 0} testid="lmsx-faculty-enrolled" />
              <Kpi icon={Trophy} label="Completions" value={data.courses_completed ?? 0} testid="lmsx-faculty-completions" />
              <Kpi icon={Award} label="Certificates" value={data.certificates_issued ?? 0} testid="lmsx-faculty-certs" />
            </div>

            <section
              data-testid="lmsx-faculty-heatmap"
              className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
            >
              <h2 className="text-sm font-bold uppercase tracking-[0.22em] text-[#0A192F] flex items-center gap-2">
                <School size={14} className="text-[#D4AF37]" /> Delegation readiness heatmap
              </h2>
              <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-2">
                {(data.readiness_heatmap || []).length === 0 ? (
                  <EmptyState label="No delegates in your organisation yet." />
                ) : (
                  data.readiness_heatmap.map((d, i) => (
                    <div key={d.user_id || i}
                          data-testid={`lmsx-faculty-row-${i}`}
                          className="flex items-center gap-3 rounded-lg border border-slate-200 p-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-[#0A192F] truncate">{d.name}</p>
                        <p className="mt-0.5 text-[11px] text-slate-500">{d.tier}</p>
                      </div>
                      <div className="w-28">
                        <div className="h-1.5 rounded-full bg-slate-200 overflow-hidden">
                          <div className="h-full rounded-full bg-gradient-to-r from-[#0F4C81] to-[#D4AF37]"
                                style={{ width: `${d.overall_score || 0}%` }} />
                        </div>
                        <p className="mt-0.5 text-right text-[10px] text-slate-500">{d.overall_score}/100</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </section>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
